import React, { useState } from 'react';
import { ExternalLink, Bookmark, BookmarkCheck, Search, Video, BookOpen, FileText, GraduationCap, Newspaper, Headphones, Clock } from 'lucide-react';

function ResourceIcon({ type }) {
  // Les icônes sont déjà bien conçues, je les garde identiques
  const iconMap = {
    'vidéo': <Video className="h-4 w-4" />,
    'document': <FileText className="h-4 w-4" />,
    'actualité': <Clock className="h-4 w-4" />,
    'podcast': <Headphones className="h-4 w-4" />,
    'article': <Newspaper className="h-4 w-4" />,
    'livre': <BookOpen className="h-4 w-4" />,
  };

  // Adapter les couleurs aux tons bleu/indigo/violet utilisés dans l'application
  const styles = {
    'vidéo': 'bg-blue-100 text-blue-700 border-blue-200',
    'document': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'actualité': 'bg-purple-100 text-purple-700 border-purple-200',
    'podcast': 'bg-purple-100 text-purple-700 border-purple-200',
    'article': 'bg-blue-100 text-blue-700 border-blue-200',
    'livre': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'default': 'bg-gray-100 text-gray-700 border-gray-200'
  };

  const labels = {
    'vidéo': 'Vidéo',
    'document': 'Document',
    'actualité': 'Actualité',
    'podcast': 'Podcast',
    'article': 'Article',
    'livre': 'Livre',
    'default': 'Page Web'
  };

  return (
    <div className="flex items-center gap-2">
      {iconMap[type] || <FileText className="h-4 w-4" />}
      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${styles[type] || styles.default}`}>
        {labels[type] || labels.default}
      </span>
    </div>
  );
}

function ResourceItem({ resource, isBookmarked, toggleSaveResource }) {
  const handleConsult = () => {
    if (resource.url !== '#') {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Formater le score pour éviter NaN
  const displayScore = typeof resource.relevanceScore === 'number' && !isNaN(resource.relevanceScore) 
    ? resource.relevanceScore 
    : 0;

  // Formatage de la date pour les actualités si disponible
  const displayDate = resource.type === 'actualité' && resource.datePublished
    ? new Date(resource.datePublished).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : null;

  return (
    <div className="p-6 h-full flex flex-col bg-white rounded-xl shadow hover:shadow-lg transition-all duration-200 border border-gray-100">
      <div className="flex justify-between items-center mb-3">
        <ResourceIcon type={resource.type} />
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 text-xs border rounded-full bg-gray-50">
            {resource.language === 'fr' ? 'Français' : 'English'}
          </span>
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-100">
            Score: {displayScore}
          </span>
        </div>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700 bg-blue-50 px-3 py-1 rounded-md border border-blue-100">
          {resource.source}
        </span>
        {displayDate && (
          <span className="text-sm text-gray-500">
            {displayDate}
          </span>
        )}
      </div>

      <h3 className="text-xl font-semibold mb-3 text-gray-800 line-clamp-2 hover:line-clamp-none transition-all">
        {resource.title}
      </h3>

      <p className="text-base text-gray-600 flex-grow mb-4 line-clamp-3 hover:line-clamp-none transition-all">
        {resource.extract || resource.snippet || 'Aucun résumé disponible'}
      </p>
      
      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <button
          onClick={() => toggleSaveResource && toggleSaveResource(resource)}
          className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded-lg transition-colors text-sm"
          title={isBookmarked ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          disabled={!toggleSaveResource}
        >
          {isBookmarked ? (
            <>
              <BookmarkCheck className="text-blue-600" size={20} />
              <span className="text-blue-600">Sauvegardé</span>
            </>
          ) : (
            <>
              <Bookmark className="text-gray-400" size={20} />
              <span className="text-gray-600">Sauvegarder</span>
            </>
          )}
        </button>

        <button
          onClick={handleConsult}
          disabled={resource.url === '#'}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm
            ${resource.url === '#' 
              ? 'text-gray-400 bg-gray-100' 
              : 'text-blue-700 hover:text-blue-800 hover:bg-blue-50 bg-blue-100'}`}
        >
          <ExternalLink size={16} />
          Consulter
        </button>
      </div>
    </div>
  );
}

export default function ResourceList({ resources, savedResources, toggleSaveResource }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const types = ['vidéo', 'document', 'actualité', 'podcast', 'article', 'livre'];
  const typeLabels = {
    'vidéo': 'Vidéo',
    'document': 'Document',
    'actualité': 'Actualité',
    'podcast': 'Podcast',
    'article': 'Article',
    'livre': 'Livre',
  };

  // Extraire les codes de langue uniques disponibles
  const availableLanguages = React.useMemo(() => {
    const uniqueLangCodes = new Set(resources?.map(r => r.language?.code).filter(Boolean));
    return ['all', ...uniqueLangCodes];
  }, [resources]);

  // Fonction pour obtenir le nom localisé de la langue
  const getLanguageName = React.useCallback((langCode) => {
    if (langCode === 'all') return 'Toutes les langues';
    
    try {
      const displayNames = new Intl.DisplayNames(['fr'], { type: 'language' });
      return displayNames.of(langCode);
    } catch (error) {
      return langCode;
    }
  }, []);

  const filteredResources = resources?.filter(resource => {
    // Vérifier si resource existe et resource.title existe
    if (!resource) return false;
    
    const titleText = resource.title || '';
    const snippetText = resource.snippet || resource.extract || '';
    
    const searchText = (titleText + snippetText)
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
      
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    const matchesLanguage = selectedLanguage === 'all' || 
                            resource.language === selectedLanguage || 
                            (resource.language && resource.language.code === selectedLanguage);
    
    return searchText && matchesType && matchesLanguage;
  });

  if (!resources?.length) {
    return (
      <p className="text-center text-gray-600 mt-8">
        Aucune ressource trouvée pour cet objectif.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-2.5 text-blue-600" size={20} />
            <input
              type="text"
              placeholder="Rechercher dans les ressources..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-blue-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-4">
            <select
              className="px-4 py-2 rounded-lg border border-blue-200 bg-white text-gray-700"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">Tous les types</option>
              {types.map(type => (
                <option key={type} value={type}>{typeLabels[type]}</option>
              ))}
            </select>
            <select
              className="px-4 py-2 rounded-lg border border-blue-200 bg-white text-gray-700"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {availableLanguages.map(lang => (
                <option key={lang} value={lang}>
                  {getLanguageName(lang)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources?.map((resource, index) => (
          <ResourceItem
            key={index}
            resource={resource}
            isBookmarked={savedResources?.some(r => r.url === resource.url)}
            toggleSaveResource={toggleSaveResource}
          />
        ))}
      </div>
    </div>
  );
}
